export interface SaveableFile {
  blob: Blob;
  filename: string;
}

type SaveFilesResult = 'saved' | 'cancelled' | 'unsupported' | 'empty';

interface DirectoryPickerOptions {
  mode?: 'read' | 'readwrite';
}

interface WritableFileHandle {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

interface DirectoryHandle {
  getFileHandle: (
    filename: string,
    options?: { create?: boolean },
  ) => Promise<{ createWritable: () => Promise<WritableFileHandle> }>;
}

interface DirectoryPickerWindow extends Window {
  showDirectoryPicker?: (options?: DirectoryPickerOptions) => Promise<DirectoryHandle>;
}

function supportsDirectoryPicker(): boolean {
  return typeof (window as DirectoryPickerWindow).showDirectoryPicker === 'function';
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Saves multiple files into a single user-selected directory.
 *
 * The directory picker only opens while the browser has transient user
 * activation, so this function calls `showDirectoryPicker` before awaiting
 * anything else. File payloads are supplied through the `loadFiles` callback
 * and fetched only after the directory is chosen, which keeps the picker call
 * inside the originating user gesture. Callers must therefore invoke this
 * function synchronously from the gesture handler, with no awaits before it.
 */
export async function saveFilesToDirectory(
  loadFiles: () => Promise<SaveableFile[]>,
): Promise<SaveFilesResult> {
  if (!supportsDirectoryPicker()) {
    return 'unsupported';
  }

  const directoryPickerWindow = window as DirectoryPickerWindow;

  let directoryHandle: DirectoryHandle | undefined = undefined;
  try {
    directoryHandle = await directoryPickerWindow.showDirectoryPicker?.({ mode: 'readwrite' });
  } catch (error) {
    if (isAbortError(error)) {
      return 'cancelled';
    }

    throw error;
  }

  if (directoryHandle === undefined) {
    return 'unsupported';
  }

  const files = await loadFiles();
  if (files.length === 0) {
    return 'empty';
  }

  for (const file of files) {
    const fileHandle = await directoryHandle.getFileHandle(file.filename, { create: true });
    const writable = await fileHandle.createWritable();

    await writable.write(file.blob);
    await writable.close();
  }

  return 'saved';
}
